import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';

import { AsideLeft, Footer, Loading, Success, StringHelpers, ArrayHelpers, SystemText } from 'e4shared';

import Feedback from 'components/asideRight/components/feedback';
import Header from 'components/header';
import AsideRight from 'components/asideRight';

import { menus } from 'data/menus';

import { loginRedirectFrom, loadUserFromLocalStorage, logout } from 'actions/user';
import { setShowSuccess, toggleLeftAside, toggleLeftAsideMenu } from 'actions/ui';

import { clearError, clearProfile } from 'actions/profile';
import { hasModule } from 'helpers/profile';

import Package from '../../../../package.json';
import config from 'config';

import './main.css';
import 'e4shared/lib/shared-bundle.css';
import { getStorage, setStorage } from 'helpers/storage';

import DebugHelper from 'components/debugHelper';
import { posthog } from 'posthog-js';

export const Main = props => {
    const dispatch = useDispatch();

    const [sideStyle, setSideStyle] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const { user, working } = useSelector(state => state.user);
    const { leftAsideOpen, leftAsideMenusOpen, exploreAsideOpen, filterAsideOpen, rightAsideOpen } = useSelector(
        state => state.ui
    );

    const st = (path, ...rest) => SystemText(`General/Footer/${path}`, ...rest);

    const translations = {
        footer: {
            version: st('Version'),
            copyright: st('Copyright'),
            allRights: st('AllRights'),
        },
    };

    React.useEffect(() => {
        if (exploreAsideOpen || filterAsideOpen) {
            setSideStyle('wide');
        }

        if (rightAsideOpen) {
            setSideStyle('');
        }
    }, [exploreAsideOpen, filterAsideOpen, rightAsideOpen]);

    React.useEffect(() => {
        if (document) {
            const sidebarOpen = leftAsideOpen ? '' : 'sidebar-open';
            const wideOpen = exploreAsideOpen ? 'wide-open' : '';
            document.body.className = `e4 ${sidebarOpen} ${wideOpen}`;
        }
    }, [leftAsideOpen, document]);

    React.useEffect(() => {
        if ((getStorage('user') || getStorage('tempUser')) && document.TRANSLATIONS?.length) {
            setLoading(false);
        }
    }, [localStorage.user, localStorage.tempUser, document.TRANSLATIONS]);

    React.useEffect(() => {
        if (user?.id && !getStorage('user') && !getStorage('tempUser')) {
            dispatch(logout(props.history, user.authority));
        }
    }, [localStorage.user, localStorage.tempUser, user?.id]);

    React.useEffect(() => {
        window?.scrollTo && window.scrollTo(0, 0);

        if (!user) {
            dispatch(loginRedirectFrom(`${window.location.pathname}${window.location.search}`));
            dispatch(loadUserFromLocalStorage(props.history));
        }

        return () => {
            dispatch(setShowSuccess(false));
            dispatch(clearError());
            dispatch(clearProfile());
        };
    }, []);

    if (!user || working) return null;

    const options = { path: '/', secure: true, sameSite: true };
    const lastVersion = getStorage('last-version');

    if (
        StringHelpers.isLaterVersion(user.version, lastVersion) &&
        StringHelpers.isLaterVersion(user.version, Package.version)
    ) {
        cookie.save('last-version', user.version, options);
        window.location.reload();
    }

    const filteredMenus = menus.reduce((all, m) => {
        const top = m.title;
        m = { ...m, trackingName: m.title, title: SystemText(`Menus/${m.title}`) };
        m.children = m.children
            ? m.children
                  .filter(c => !c.module || hasModule(user, c.module))
                  .filter(c => !c.lms || user.lms_Types?.some(t => t === c.lms))
                  .map(c => ({
                      ...c,
                      trackingName: `${m.title}>${c.title}`,
                      title: SystemText(`Menus/${top}/${c.title}`),
                  }))
            : null;
        return !m.module || hasModule(user, m.module) ? all.concat([m]) : all;
    }, []);

    return loading ? (
        <Loading />
    ) : (
        <div
            className={`e4-container hold-transition skin-blue sidebar-mini ${leftAsideOpen ? '' : 'sidebar-collapse'} ${
                user.partner_Id > 0 || user.spoofing ? 'partner-portal' : ''
            }`}
        >
            <Feedback />
            <DebugHelper />
            <Header />
            <Success setShowSuccess={setShowSuccess} />
            <AsideLeft
                location={location}
                Link={Link}
                history={history}
                menus={filteredMenus}
                user={user}
                leftAsideMenusOpen={leftAsideMenusOpen}
                toggleLeftAsideMenu={id => dispatch(toggleLeftAsideMenu(id))}
                toggleLeftAside={() => dispatch(toggleLeftAside())}
                showSearch
                config={config}
                trackEvent={trackingName =>
                    //use beacon as we have page navigation so xhr can be cancelled
                    posthog.capture('left-menu-click', { menuItem: trackingName }, { transport: 'sendBeacon' })
                }
            />
            <div id="content" className="content-wrapper">
                {filterAsideOpen && <div className="content-overlay" />}
                {exploreAsideOpen && <div className="explore-open" />}
                {props.children}
            </div>
            <Footer version={Package.version} text={translations.footer} />
            <AsideRight sideStyle={sideStyle} />
            <div className={`control-sidebar-bg ${sideStyle}`} />
        </div>
    );
};

export default withRouter(Main);
